import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { reportClientError } from '../lib/error-report';

/**
 * Shown while auth bootstrap is in flight (status 'unknown', or authenticated
 * but /v1/me hasn't landed yet). If it STALLS — Firebase's onAuthStateChanged
 * never firing on a network/extension that blocks its domains, or /v1/me hanging
 * — this self-heals into a recovery panel instead of a permanent white screen
 * (the "after signing in, the app doesn't appear; demo works" report). A demo
 * session needs no Firebase, which is why it was unaffected.
 */
function BootstrapPending({ reason }: { reason: string }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setSlow(true);
      // Signal so we can see how often/where bootstrap stalls in production.
      reportClientError(new Error(`auth bootstrap stalled (${reason})`), {
        kind: 'bootstrap-stall',
      });
    }, 8000);
    return () => clearTimeout(t);
  }, [reason]);

  const box: React.CSSProperties = {
    minHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    textAlign: 'center',
    padding: '2rem',
  };
  return (
    <div style={box}>
      <style>{'@keyframes kb-spin{to{transform:rotate(360deg)}}'}</style>
      <div style={{ fontSize: 11, color: '#6e6c66', letterSpacing: 1.5 }}>KEOBOOKS</div>
      {!slow ? (
        <>
          <div
            aria-label="Loading"
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '3px solid #e4e2da',
              borderTopColor: '#1f7a3f',
              animation: 'kb-spin 0.8s linear infinite',
            }}
          />
          <p style={{ color: '#5f5e5a', fontSize: 14, margin: 0 }}>Loading your workspace…</p>
        </>
      ) : (
        <>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: '#1e3a5f', margin: 0 }}>
            This is taking longer than expected
          </h1>
          <p style={{ color: '#5f5e5a', fontSize: 14, maxWidth: 440, lineHeight: 1.55, margin: 0 }}>
            We couldn&rsquo;t finish loading your account. Check your connection and reload — if it
            keeps happening, sign in again. (A firewall or browser extension blocking Google sign-in
            can cause this.)
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 18px',
                borderRadius: 8,
                border: '1px solid #1f7a3f',
                background: '#1f7a3f',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload
            </button>
            <a
              href="/signin"
              style={{
                padding: '10px 18px',
                borderRadius: 8,
                border: '1px solid #d3d1c7',
                color: '#1e3a5f',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              Sign in again
            </a>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Auth + onboarding-state router guard. Four states:
 *
 *   1. status === 'unknown'      → bootstrap in progress; render nothing
 *   2. unauthenticated           → bounce to /signin
 *   3. authenticated, no 2FA     → /settings (enroll TOTP before going further)
 *   4. authenticated + 2FA:
 *        a. no client linked              → /onboarding
 *        b. client without engagement let → /engagement-letter
 *        c. fully set up                  → /dashboard (any other path → here)
 *
 * The 2FA gate (step 3) makes TOTP mandatory for every account in IdP mode.
 * The Settings page already has the enrollment UI + the staff-aware "sign
 * out and back in" panel; for customers we just need them in there too.
 * Demo sessions and dev-mode signups bypass — there's no Firebase user to
 * enroll in those flows.
 *
 * The guard never traps you on a step you've already passed.
 */
export function GuidedRoute() {
  const { status, currentClient, isDemo, user } = useAuth();
  const location = useLocation();

  if (status === 'unknown') return <BootstrapPending reason="status-unknown" />;
  if (status === 'unauthenticated') {
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  }

  // Authenticated, but some sign-in paths flip status optimistically before the
  // bootstrap /v1/me populates the user + clients. Hold until it lands — rendering
  // a tenant-scoped page now would fire its first fetch before the current client
  // is known, scoping it to a stale X-Client-Id (→ 404 on every page).
  if (!user) return <BootstrapPending reason="user-null" />;

  // status === 'authenticated'
  const path = location.pathname;

  // The referral partner surface is not tenant-scoped: a partner may have no
  // client of their own (anyone can be a partner). Let it through the
  // onboarding / MFA / engagement gates so pure partners can reach it.
  if (path === '/referral') return <Outlet />;

  // An invitee mid-accept (invite token stashed, no client of their own yet)
  // belongs on the /invite accept page — not the new-business onboarding flow.
  if (!currentClient) {
    let pendingInvite: string | null = null;
    try {
      pendingInvite = window.localStorage.getItem('kb_pending_invite');
    } catch {
      /* ignore */
    }
    if (pendingInvite) return <Navigate to="/invite" replace />;
  }
  // Mandatory TOTP. Customer or staff, both must enroll. Demo sessions skip
  // (no real Firebase identity to enroll under); dev-auth signups don't pass
  // through Firebase either, so user.mfaEnrolled stays false — the
  // firebase-enabled check keeps local dev off the TOTP gate.
  const needsMfaEnrollment = !isDemo && user && !user.mfaEnrolled;
  const needsOnboarding = !currentClient;
  // Demo sessions skip the engagement-letter gate — there is no legal
  // engagement to sign for ephemeral sample data.
  const needsEngagementLetter = !isDemo && currentClient && !currentClient.engagementLetterAccepted;

  // Sequential, TERMINAL gates: each one either renders its own page (Outlet)
  // or redirects to it, and never falls through to the next gate. The previous
  // version let the MFA and onboarding gates both fire for a brand-new user who
  // needed both, ping-ponging /settings <-> /onboarding forever — an infinite
  // redirect that rendered null and blanked the page.
  //
  // Order: onboarding first (a client must exist before anything else, and TOTP
  // enrollment can't happen until the email is verified anyway), then mandatory
  // TOTP, then the engagement letter.
  if (needsOnboarding) {
    return path === '/onboarding' ? <Outlet /> : <Navigate to="/onboarding" replace />;
  }
  if (needsMfaEnrollment) {
    return path === '/settings' ? (
      <Outlet />
    ) : (
      <Navigate to="/settings" replace state={{ mfaRequired: true }} />
    );
  }
  if (needsEngagementLetter) {
    return path === '/engagement-letter' ? (
      <Outlet />
    ) : (
      <Navigate to="/engagement-letter" replace />
    );
  }

  // Billing-lapse gate. A client whose subscription has truly ended —
  // 'canceled' (period lapsed) or 'frozen' (access suspended) — is sent to
  // /reactivate. 'past_due' is deliberately NOT gated: it's transient while
  // Stripe retries the card during dunning, and locking the customer out mid-
  // retry would be punitive — the dunning emails + Settings billing card handle
  // it, and if dunning is exhausted Stripe moves the sub to 'canceled' (which is
  // gated). A *scheduled* period-end cancellation keeps the client 'active'
  // until it lapses, so it never trips this. Demo sessions are exempt.
  // /reactivate (the recovery screen) and /referral (not tenant-scoped) pass.
  const lapsed =
    !isDemo &&
    !!currentClient &&
    (currentClient.status === 'canceled' || currentClient.status === 'frozen');
  if (lapsed && path !== '/reactivate' && path !== '/referral') {
    return <Navigate to="/reactivate" replace />;
  }
  // A healthy client should never sit on the recovery screen.
  if (path === '/reactivate' && !lapsed) {
    return <Navigate to="/dashboard" replace />;
  }

  // Fully set up — keep entry / already-completed setup paths on the dashboard.
  if (path === '/' || path === '/onboarding' || path === '/engagement-letter') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
